import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Key, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { profileApi } from '../../services/api';
import { useDialog } from '../../hooks/useDialog';

const ChangePasswordPage: React.FC = () => {
  const { t } = useTranslation('app');
  const navigate = useNavigate();
  const { alert } = useDialog();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const getPasswordStrength = (password: string): number => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 6) s++;
    if (password.length >= 8) s++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) s++;
    if (/\d/.test(password)) s++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) s++;
    return Math.min(s, 4);
  };

  const strength = getPasswordStrength(formData.newPassword);
  const strengthColors = ['bg-border-subtle', 'bg-danger', 'bg-warning', 'bg-warning', 'bg-action-primary'];

  const inputCls = 'w-full rounded-[8px] border border-border-default bg-bg-surface px-3 py-[9px] text-[12.5px] text-text-primary outline-none transition-colors placeholder:text-text-muted focus:border-action-primary focus:ring-2 focus:ring-action-primary-border';
  const labelCls = 'mb-[6px] block font-mono text-[10.5px] text-text-muted';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      await alert(t('changePassword.passwordMismatch'), t('changePassword.title'), 'danger');
      return;
    }
    setLoading(true);
    try {
      await profileApi.changePassword({ currentPassword: formData.currentPassword, newPassword: formData.newPassword });
      await alert(t('changePassword.success'), t('changePassword.title'), 'success');
      navigate(-1);
    } catch (error: any) {
      await alert(error.response?.data?.message || t('changePassword.error'), t('changePassword.title'), 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      <div className="mb-6 rounded-[12px] border border-border-subtle bg-bg-surface p-5">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex h-[56px] w-[56px] items-center justify-center rounded-[16px] bg-primary-soft text-primary-ink">
            <Key className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-[20px] font-semibold text-text-primary">{t('changePassword.title')}</h1>
            <p className="mt-1 text-[12.5px] text-text-muted">{t('changePassword.subtitle')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>{t('changePassword.currentPassword')}</label>
            <input type="password" value={formData.currentPassword} onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })} className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>{t('changePassword.newPassword')}</label>
            <input type="password" value={formData.newPassword} onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })} className={inputCls} required />
            <p className="mt-1 text-[12px] text-text-muted">{t('changePassword.newPasswordHint')}</p>
          </div>

          {formData.newPassword && (
            <div className="space-y-2">
              <label className="block font-mono text-[10.5px] text-text-muted">{t('changePassword.strength')}</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className={`h-2 flex-1 rounded-full ${i <= strength ? strengthColors[strength] : 'bg-border-subtle'}`} />
                ))}
              </div>
              {strength > 0 && <p className="text-[12px] text-text-muted">{t(`changePassword.strengthLevels.${strength}`)}</p>}
            </div>
          )}

          <div>
            <label className={labelCls}>{t('changePassword.confirmPassword')}</label>
            <input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} className={inputCls} required />
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => navigate(-1)} className="cursor-pointer rounded-[8px] border border-border-default bg-bg-surface px-4 py-2 text-[13px] font-medium text-text-primary hover:bg-bg-elevated">
              {t('changePassword.cancel')}
            </button>
            <button type="submit" disabled={loading} className="cursor-pointer rounded-[8px] bg-action-primary px-4 py-2 text-[13px] font-semibold text-white hover:bg-action-primary-hover disabled:opacity-50">
              {loading ? t('changePassword.submitting') : t('changePassword.submit')}
            </button>
          </div>
        </form>
      </div>

      <div className="rounded-[12px] border border-action-primary-border bg-primary-soft p-4">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5 text-primary-ink" />
          <div>
            <h3 className="mb-2 text-[13px] font-semibold text-primary-ink">{t('changePassword.tips.title')}</h3>
            <ul className="space-y-1 text-[12.5px] text-text-secondary">
              <li>• {t('changePassword.tips.1')}</li>
              <li>• {t('changePassword.tips.2')}</li>
              <li>• {t('changePassword.tips.3')}</li>
              <li>• {t('changePassword.tips.4')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
